import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PiDownload } from "react-icons/pi";
import { Button } from "@/components/custom/button";
import {
  DataTableContext,
  PaginationRequest,
} from "@/components/custom/data-table/data-table-context";
import { useTableFilterQuery } from "@/hooks/use-finalQuery";
import { usePermissions } from "@/hooks/use-permissions";
import { ApiError, PaginatedResponseWithCountDto } from "@/client/extend";
import { QueryKey } from "@tanstack/react-query";
import { FieldPath } from "react-hook-form";
import { useSearch } from "@tanstack/react-router";

async function downloadCsv(
  url: string,
  query: unknown,
  fileName: string,
  accountId: number | undefined,
) {
  const requestUrl = new URL(url, window.location.origin);
  if (accountId !== undefined) {
    // Sent as `viewAccountId`, not `accountId` -- some endpoints have their own, unrelated
    // `accountId` query field (e.g. ClustersController.listMinimal's), and a shared name would
    // silently collide with it. See client/index.ts's request interceptor for the same rename.
    requestUrl.searchParams.set("viewAccountId", String(accountId));
  }
  const res = await fetch(requestUrl, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ query }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => undefined);
    throw new Error(body?.message ?? "Failed to download CSV");
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fileName;
  // Required for Firefox — clicking an anchor not attached to the document is a no-op there.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

export function DownloadCsvButton<
  TRow = unknown,
  TArgument extends PaginationRequest = PaginationRequest,
  TQueryFnData extends
    PaginatedResponseWithCountDto<TRow> = PaginatedResponseWithCountDto<TRow>,
  TError = ApiError,
  TData extends TQueryFnData = TQueryFnData,
  TQueryKey extends QueryKey = any,
  TSearchField extends FieldPath<TArgument> = FieldPath<TArgument>,
  TSortField extends FieldPath<TArgument> = FieldPath<TArgument>,
>({
  context,
}: {
  context: DataTableContext<
    TRow,
    TArgument,
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TSearchField,
    TSortField
  >;
}) {
  const { t } = useTranslation();
  const { permissions } = usePermissions();
  const [isDownloading, setIsDownloading] = useState(false);
  const query = useTableFilterQuery(context);
  // strict: false + select so this re-renders only when accountId itself changes, not on
  // every unrelated filter/pagination search-param change from the table this button sits in.
  const accountId = useSearch({
    strict: false,
    select: (search: any) => search.accountId as number | undefined,
  });
  const config = context.downloadCsv;
  const hasPermission = !config?.permission || config.permission(permissions);

  if (!config || !hasPermission) {
    return null;
  }

  const onClick = async () => {
    setIsDownloading(true);
    try {
      const fileName = `${config.fileName}-${new Date().toISOString().slice(0, 10)}.csv`;
      await downloadCsv(config.url(), query, fileName, accountId);
    } catch {
      toast.error(t("Failed to download CSV"));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      onClick={onClick}
      loading={isDownloading}
      type="button"
      className="flex gap-1 justify-around"
    >
      <PiDownload className="w-4 h-4" />
      {t("Download")}
    </Button>
  );
}
